// src/pages/DoctorReviewsModerationPage.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Typography, Stack, Divider, CircularProgress,
  Button, Avatar, Rating, alpha, useTheme, IconButton, Tooltip
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

const DoctorReviewsModerationPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const token = localStorage.getItem('authToken');

  const loadPending = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/doctor/reviews/pending/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setPending(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('No se pudieron cargar las opiniones pendientes');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moderate = async (reviewId, estado) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/doctor/reviews/${reviewId}/moderate/`,
        { estado },
        { headers: { Authorization: `Token ${token}` } }
      );
      setPending(prev => prev.filter(r => r.id !== reviewId));
      toast.success(estado === 'aprobado' ? 'Opinión aprobada' : 'Opinión rechazada');
    } catch (e) {
      toast.error('No se pudo actualizar la opinión');
      console.error(e);
    }
  };

  return (
    <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: 'none'
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="Volver">
                <IconButton
                  aria-label="Volver"
                  onClick={() => navigate(-1)}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    borderRadius: 2,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) }
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                <RateReviewIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">Moderar Opiniones</Typography>
                <Typography variant="body2" color="text.secondary">
                  Revisa y aprueba o rechaza las opiniones de tus pacientes
                </Typography>
              </Box>
            </Stack>

            <Tooltip title="Actualizar">
              <span>
                <IconButton
                  onClick={async () => {
                    setRefreshing(true);
                    await loadPending();
                    setRefreshing(false);
                  }}
                  disabled={refreshing || loading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : pending.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary">
                No hay opiniones pendientes por ahora.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Las nuevas opiniones aparecerán aquí para moderación.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {pending.map((r) => (
                <Paper
                  key={r.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.01)
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
                      {r.nombre?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2" fontWeight="bold">{r.nombre}</Typography>
                        <Rating value={r.rating} readOnly size="small" />
                        <Typography variant="caption" color="text.secondary">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {r.comentario}
                      </Typography>

                      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ThumbUpAltIcon />}
                          onClick={() => moderate(r.id, 'aprobado')}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<ThumbDownAltIcon />}
                          onClick={() => moderate(r.id, 'rechazado')}
                        >
                          Rechazar
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default DoctorReviewsModerationPage;
